import { z } from "zod";
import {
  CURRENCY_CONFIG,
  SUPPORTED_CURRENCIES,
  type BudgetFlexibility,
  type SupportedCurrency,
} from "./quizConstants";

// Draft types reflect React state (nullable until filled in by the user).
// Validation schemas express what "complete" means for each step — used only
// for the canContinue boolean gate via safeParse(...).success.

export type TripProfileDraft = {
  childrenUnder12: number | null;
  soloTraveller: boolean | null;
  hasDisabilities: boolean | null;
  disabilityMobility: boolean;
  disabilityVisual: boolean;
  disabilityHearing: boolean;
};

export type PriorityKey = "wildlife" | "authenticity" | "service" | "eco" | "luxury" | "value";
export type PrioritiesDraft = Record<PriorityKey, number>;

/**
 * Canonical ordered list of safari priority dimensions with their display labels.
 * This is the single source of truth — import here instead of re-defining locally.
 */
export const DIMENSIONS: ReadonlyArray<{ key: PriorityKey; label: string }> = [
  { key: "wildlife",     label: "Big 5 Sightings"          },
  { key: "authenticity", label: "Authenticity"              },
  { key: "service",      label: "Service & Guiding Quality" },
  { key: "eco",          label: "Sustainability"            },
  { key: "luxury",       label: "Luxury"                   },
  { key: "value",        label: "Value for money"          },
];

/** Derived from DIMENSIONS so labels never drift. */
export const DIMENSION_LABELS: Record<PriorityKey, string> = Object.fromEntries(
  DIMENSIONS.map(({ key, label }) => [key, label]),
) as Record<PriorityKey, string>;

export type ConstraintsDraft = {
  // null until the user has touched the slider — see RangeSlider.tsx and
  // QuizStepConstraints.tsx. buildConstraintsSchema below rejects null, so
  // canContinue stays false (and no PATCH is ever sent) until a real value
  // is chosen.
  budget: number | null;
  currency: SupportedCurrency;
  // Never null — defaults to "max" (DEFAULT_BUDGET_FLEXIBILITY), so it can
  // neither block nor satisfy the continue gate.
  budgetFlexibility: BudgetFlexibility;
  preferredCountry: string | null;
  preferredRegion: string | null;
};

export const priorityField = z.number().int().min(0).max(3);

export const prioritiesSchema = z.object({
  wildlife: priorityField,
  authenticity: priorityField,
  service: priorityField,
  eco: priorityField,
  luxury: priorityField,
  value: priorityField,
});

// Factory so budget bounds are currency-aware.
export function buildConstraintsSchema(currency: SupportedCurrency) {
  const cfg = CURRENCY_CONFIG[currency]!;
  return z.object({
    budget: z.number().int().min(cfg.min).max(cfg.max),
    currency: z.enum(SUPPORTED_CURRENCIES),
  });
}

/**
 * Single source of truth for "what's left unanswered on step 2" — consumed
 * by both useQuizWizardController's canContinue gate and
 * QuizStepConstraints' blocked-submit reveal. Deriving these independently
 * in two places drifted twice (the disability sub-type condition, then the
 * budget range check) before this existed — don't reintroduce a second
 * derivation.
 */
export function missingConstraintFields(
  constraints: ConstraintsDraft,
  trip: TripProfileDraft,
) {
  return {
    budget: !buildConstraintsSchema(constraints.currency).safeParse(constraints).success,
    children: trip.childrenUnder12 === null,
    disabilities: trip.hasDisabilities === null,
    disabilityType:
      trip.hasDisabilities === true &&
      !trip.disabilityMobility &&
      !trip.disabilityVisual &&
      !trip.disabilityHearing,
  };
}
